import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// items: [{ src, alt, caption? }], index: number | null
export const Lightbox = ({ items, index, onChange, onClose, testId = "gallery-lightbox" }) => {
  const open = index !== null && index !== undefined && items[index];
  const many = items.length > 1;
  const prev = () => onChange((index + items.length - 1) % items.length);
  const next = () => onChange((index + 1) % items.length);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (many && e.key === "ArrowLeft") prev();
      if (many && e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={testId}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[80] glass-dark flex items-center justify-center p-4 sm:p-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <button data-testid="lightbox-close" aria-label="Close" className="absolute top-6 right-6 text-amber-200 hover:text-amber-50 p-2">
            <X size={26} />
          </button>
          {many && (
            <button
              data-testid="lightbox-prev"
              aria-label="Previous photo"
              className="absolute left-4 sm:left-10 text-amber-200 hover:text-amber-50 p-2"
              onClick={(e) => { e.stopPropagation(); prev(); }}
            >
              <ChevronLeft size={32} />
            </button>
          )}
          <motion.figure
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35 }}
            className="flex flex-col items-center max-w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[index].src}
              alt={items[index].alt}
              className="max-h-[78vh] max-w-full object-contain border border-amber-500/25"
            />
            {items[index].caption && (
              <figcaption data-testid="lightbox-caption" className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-300/90 text-center">
                {items[index].caption}
              </figcaption>
            )}
          </motion.figure>
          {many && (
            <button
              data-testid="lightbox-next"
              aria-label="Next photo"
              className="absolute right-4 sm:right-10 text-amber-200 hover:text-amber-50 p-2"
              onClick={(e) => { e.stopPropagation(); next(); }}
            >
              <ChevronRight size={32} />
            </button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
